import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import DataTable, { DataTableColumnHeader } from '../../ui/DataTable'
import Badge from '../../ui/Badge'
import StatusDot from './StatusDot'
import DefaultBadge from './DefaultBadge'
import { categoryBadgeVariant, categoryLabel } from './constants'

const EMPTY_DEFAULT = (
  <div>
    <strong>Aucune donnée</strong>
    <p>Les statuts configurés apparaîtront ici une fois créés.</p>
  </div>
)

const EMPTY_SEARCH = (
  <div>
    <strong>Aucun résultat</strong>
    <p>Aucun statut ne correspond à votre recherche.</p>
  </div>
)

const renderStatutsHeader = ({ column }) => <DataTableColumnHeader column={column} title="Statuts" />
const renderCategorieHeader = ({ column }) => <DataTableColumnHeader column={column} title="Catégorie" />

const renderStatutCell = ({ row }) => (
  <span className="tw:inline-flex tw:items-center tw:gap-2">
    <StatusDot color={row.original.color} label={row.original.name} />
    {row.original.is_default ? <DefaultBadge /> : null}
  </span>
)

const renderCategorieCell = ({ row }) => (
  <Badge variant={categoryBadgeVariant(row.original.category)}>
    {categoryLabel(row.original.category)}
  </Badge>
)

const COLUMNS = [
  {
    id: 'name',
    accessorKey: 'name',
    enableSorting: true,
    header: renderStatutsHeader,
    cell: renderStatutCell
  },
  {
    id: 'category',
    accessorKey: 'category',
    enableSorting: true,
    header: renderCategorieHeader,
    cell: renderCategorieCell
  }
]

const StatusesTable = ({ statuses, loading, error, searching, sort, direction, onSortChange }) => {
  const sortingState = useMemo(
    () => sort ? [{ id: sort, desc: direction === 'desc' }] : [], [sort, direction]
  )

  const handleSortingChange = useCallback(updater => {
    const next = typeof updater === 'function' ? updater(sortingState) : updater
    if (next.length === 0) {
      onSortChange(null, 'asc')
      return
    }
    const [{ id, desc }] = next
    onSortChange(id, desc ? 'desc' : 'asc')
  }, [sortingState, onSortChange])

  const table = useReactTable({
    data: statuses,
    columns: COLUMNS,
    state: { sorting: sortingState },
    onSortingChange: handleSortingChange,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: row => String(row.id)
  })

  return (
    <DataTable
      emptyState={searching ? EMPTY_SEARCH : EMPTY_DEFAULT}
      error={error}
      loading={loading}
      table={table}
    />
  )
}

StatusesTable.propTypes = {
  direction: PropTypes.oneOf(['asc', 'desc']),
  error: PropTypes.instanceOf(Error),
  loading: PropTypes.bool.isRequired,
  onSortChange: PropTypes.func.isRequired,
  searching: PropTypes.bool,
  sort: PropTypes.string,
  statuses: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    color: PropTypes.string,
    category: PropTypes.string
  })).isRequired
}

StatusesTable.defaultProps = {
  direction: 'asc',
  error: null,
  searching: false,
  sort: null
}

export default StatusesTable
